import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import { lottieRegistry } from './lottieRegistry';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

const hexToLottieColor = (hex) => {
  const clean = (hex || '').replace('#', '').trim();
  if (clean.length !== 6) return [1, 1, 1, 1];
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  return [r, g, b, 1];
};

const recolorLottie = (animationData, hexColor) => {
  const target = hexToLottieColor(hexColor);
  const cloned = JSON.parse(JSON.stringify(animationData));
  const stack = [cloned];

  while (stack.length) {
    const node = stack.pop();
    if (!node || typeof node !== 'object') continue;

    if (
      node.c &&
      node.c.k &&
      Array.isArray(node.c.k) &&
      node.c.k.length === 4 &&
      typeof node.c.k[0] === 'number'
    ) {
      node.c.k = target;
    }

    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) stack.push(node[i]);
    } else {
      const vals = Object.values(node);
      for (let i = 0; i < vals.length; i++) stack.push(vals[i]);
    }
  }

  return cloned;
};

const recolorCache = new Map();

const LottieIcon = ({
  iconKey,
  color = '#01454F',
  size = 60,
  loop = false,
  playKey = 0,
  visible = true,
  className = '',
}) => {
  const [baseData, setBaseData] = useState(null);
  const lottieRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loader = lottieRegistry[iconKey];
    if (!loader) {
      setBaseData(null);
      return;
    }

    loader().then((mod) => {
      if (!cancelled) setBaseData(mod.default);
    });

    return () => {
      cancelled = true;
    };
  }, [iconKey]);

  const animationData = useMemo(() => {
    if (!baseData) return null;

    const cacheKey = `${iconKey}|${color}`;
    if (recolorCache.has(cacheKey)) return recolorCache.get(cacheKey);

    const recolored = recolorLottie(baseData, color);
    recolorCache.set(cacheKey, recolored);
    return recolored;
  }, [baseData, iconKey, color]);

  const tryPlay = () => {
    if (!visible) return;

    // ref ume da bude null na prvom tick-u kad se komponenta tek mountuje
    if (!lottieRef.current) {
      requestAnimationFrame(tryPlay);
      return;
    }

    lottieRef.current.stop();
    lottieRef.current.play();
  };

  useEffect(() => {
    tryPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playKey, visible, iconKey, color, animationData]);

  if (!animationData) return null;

  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        marginBottom: '32px',
        opacity: visible ? 1 : 0,
        transition: 'opacity 250ms ease, transform 250ms ease',
        transform: visible ? 'translateY(0px)' : 'translateY(6px)',
      }}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        loop={loop}
        autoplay={false}
        style={{ width: size, height: size }}
      />
    </div>
  );
};

export default LottieIcon;
